/**
 * LEGACY reader (plots plan P, read-adapters.md): today's rating-likert bar
 * resolver, moved here unchanged from `slides/rating-likert-split.ts`.
 *
 * It draws stored rating-likert slides that carry no `plot` (decks built
 * before the diverging-likert step): the midpoint split for a payload without
 * `segments`, the measured statistics, and the stamped `segments` when a stored
 * payload has them but no plot (it lacks the stamped `net`, NPS score and
 * legend a shape-only adapter could not supply). It is never used for a newly
 * built payload and is deleted with the last stored payload of its shape.
 */
import { NPS_POLICY, RATING_POLICIES, isRatingStatistics, ratingBounds } from '@digital-twins/shared-types'
import type { RatingLikertSlideData } from '../../../slides/renderer-internal-types'
import type { RatingStatistics } from '@digital-twins/shared-types'
import {
  planRatingBar,
  ratingBandLabels,
  readRatingSegments,
} from '../bar-plan'
import type {
  RatingBand,
  RatingBandLabels,
  RatingBandSegment,
  RatingBarPlan,
} from '../bar-plan'

export type RatingBucket = RatingLikertSlideData['buckets'][number]

function bucketPosition(bucket: RatingBucket, fallback: number) {
  if (typeof bucket.rawMin === 'number' && typeof bucket.rawMax === 'number') {
    return (bucket.rawMin + bucket.rawMax) / 2
  }
  if (typeof bucket.rawMin === 'number') return bucket.rawMin
  if (typeof bucket.rawMax === 'number') return bucket.rawMax

  const parsed = Number.parseFloat(String(bucket.label).replace(/[^\d.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizedBuckets(buckets: Array<RatingBucket>) {
  const sorted = [...buckets].sort(
    (left, right) => bucketPosition(left, 0) - bucketPosition(right, 0),
  )
  const total = sorted.reduce((sum, bucket) => sum + Math.max(0, bucket.value || 0), 0)
  const multiplier = total > 0 && (total < 99 || total > 101) ? 100 / total : 1

  return sorted.map((bucket, index) => ({
    ...bucket,
    position: bucketPosition(bucket, index),
    value: Math.max(0, (bucket.value || 0) * multiplier),
  }))
}

/**
 * The legacy bar, expressed in the same shape the python builder stamps.
 *
 * Measured groups arrive as [favorable, middle, unfavorable]; a bar reads low
 * end of the scale first, and a reversed scale puts the favorable group at that
 * low end. This mirrors `_measured_band_segments` in
 * `apps/python-api/canonical_analysis/rating_segments.py` so a payload without
 * `segments` draws the same three-band bar through the same renderer instead of
 * the four-band geometry it used to carry.
 */
function bandSegmentsLowToHigh(
  labels: Array<string>,
  values: Array<number>,
  reversed: boolean,
): Array<RatingBandSegment> {
  const lowBand: RatingBand = reversed ? 'favorable' : 'unfavorable'
  const highBand: RatingBand = reversed ? 'unfavorable' : 'favorable'
  const lowIndex = reversed ? 0 : 2
  const highIndex = reversed ? 2 : 0
  return [
    { label: labels[lowIndex], value: Math.max(0, values[lowIndex] || 0), band: lowBand },
    { label: labels[1], value: Math.max(0, values[1] || 0), band: 'middle' },
    { label: labels[highIndex], value: Math.max(0, values[highIndex] || 0), band: highBand },
  ]
}

/** Plain hyphen: chart copy bars dashes. */
function rangeText(low: number, high: number): string {
  return low === high ? String(low) : `${low}-${high}`
}

/**
 * The value range each end of a legacy midpoint split covers, from the scale:
 * everything below the midpoint and everything above it.
 */
function legacyRangeLabels(
  scale: RatingLikertSlideData['scale'],
  midpoint: number,
): { low: string; high: string; lowMax: number; highMin: number } {
  const lowMax = Number.isInteger(midpoint) ? midpoint - 1 : Math.floor(midpoint)
  const highMin = Number.isInteger(midpoint) ? midpoint + 1 : Math.ceil(midpoint)
  return { low: rangeText(scale.min, lowMax), high: rangeText(highMin, scale.max), lowMax, highMin }
}

/**
 * True when the midpoint split draws exactly the canonical favorable and
 * unfavorable bands of a registered rating unit on these bounds (1-5: 4-5 and
 * 1-2), so its ends may be called favorable / unfavorable. A reversed scale
 * mirrors the policy. Every other split is a range split.
 */
function midpointSplitIsPolicy(
  scale: RatingLikertSlideData['scale'],
  ranges: { lowMax: number; highMin: number },
): boolean {
  // A middle-is-best scale has no favourable end, so a midpoint split is never its policy.
  if (scale.polarity === 'middle_is_best') {
    return false
  }
  const reversed = scale.polarity === 'lower_is_better'
  return Object.values(RATING_POLICIES).some((policy) => {
    if (policy.minimum !== scale.min || policy.maximum !== scale.max) {
      return false
    }
    const favorable = reversed ? scale.min + scale.max - policy.favorableMinimum : policy.favorableMinimum
    const unfavorable = reversed ? scale.min + scale.max - policy.unfavorableMaximum : policy.unfavorableMaximum
    return reversed
      ? favorable === ranges.lowMax && unfavorable === ranges.highMin
      : favorable === ranges.highMin && unfavorable === ranges.lowMax
  })
}

export function splitRatingBuckets(
  buckets: Array<RatingBucket>,
  scale: RatingLikertSlideData['scale'],
  displayLabels?: RatingLikertSlideData['displayLabels'],
  statistics?: RatingStatistics | null,
) {
  if (statistics != null) return splitMeasuredStatistics(statistics, scale)
  // Untyped 0–10 histograms cannot distinguish ratings from NPS. Do not
  // reinterpret historical data; explicit measured statistics above remain valid.
  if (scale.kind == null && (scale.polarity ?? 'higher_is_better') === 'higher_is_better' && scale.min === 0 && scale.max === 10) {
    return {
      dataStatus: 'unsupported' as const,
      metricLabels: [], metricValues: [], legendLabels: [], geometryLegendLabels: [],
      segments: { negativeStrong: 0, negativeSoft: 0, positiveSoft: 0, positiveStrong: 0 },
      bandSegments: [] as Array<RatingBandSegment>,
    }
  }
  const normalized = normalizedBuckets(buckets)
  if (scale.kind === 'nps') {
    // Promoter and detractor cutoffs come from the owner's policy
    // (NPS_POLICY, generated from rating_statistics.py).
    const detractors = normalized
      .filter((bucket) => bucket.position <= NPS_POLICY.unfavorableMaximum)
      .reduce((sum, bucket) => sum + bucket.value, 0)
    const passives = normalized
      .filter((bucket) => bucket.position > NPS_POLICY.unfavorableMaximum && bucket.position < NPS_POLICY.favorableMinimum)
      .reduce((sum, bucket) => sum + bucket.value, 0)
    const promoters = normalized
      .filter((bucket) => bucket.position >= NPS_POLICY.favorableMinimum)
      .reduce((sum, bucket) => sum + bucket.value, 0)

    return {
      dataStatus: normalized.some((bucket) => bucket.value > 0) ? 'available' as const : 'empty' as const,
      metricLabels: ['Promoters', 'Passives', 'Detractors'],
      metricValues: [promoters, passives, detractors],
      legendLabels: ['Promoters', 'Passive right', 'Passive left', 'Detractors'],
      geometryLegendLabels: ['Promoters', '½ passives', '½ passives', 'Detractors'],
      segments: {
        negativeStrong: detractors,
        negativeSoft: passives / 2,
        positiveSoft: passives / 2,
        positiveStrong: promoters,
      },
      bandSegments: bandSegmentsLowToHigh(
        ['Promoters', 'Passives', 'Detractors'], [promoters, passives, detractors], false,
      ),
    }
  }

  const midpoint = (scale.min + scale.max) / 2
  const lower = normalized.filter((bucket) => bucket.position < midpoint)
  const neutral = normalized.filter((bucket) => bucket.position === midpoint)
  const upper = normalized.filter((bucket) => bucket.position > midpoint)
  const lowest = lower[0]?.value || 0
  const highest = upper[upper.length - 1]?.value || 0
  const lowerTotal = lower.reduce((sum, bucket) => sum + bucket.value, 0)
  const neutralTotal = neutral.reduce((sum, bucket) => sum + bucket.value, 0)
  const upperTotal = upper.reduce((sum, bucket) => sum + bucket.value, 0)

  const reversed = scale.polarity === 'lower_is_better'
  // Without measured statistics this is a split around the midpoint. Where it
  // is not the unit's canonical favorable policy (8-10 on 1-10, 6-7 on 1-7)
  // each end is labelled with its range, never "Favorable".
  const ranges = legacyRangeLabels(scale, midpoint)
  const isPolicySplit = midpointSplitIsPolicy(scale, ranges)
  const endLabels = isPolicySplit
    ? [
      displayLabels?.favorable || 'Favorable',
      displayLabels?.neutral || 'Neutral',
      displayLabels?.unfavorable || 'Unfavorable',
    ]
    : reversed
      ? [ranges.low, displayLabels?.neutral || 'Neutral', ranges.high]
      : [ranges.high, displayLabels?.neutral || 'Neutral', ranges.low]
  const lowLabel = displayLabels?.lowRating || 'Low rating'
  const highLabel = displayLabels?.highRating || 'High rating'
  // Center neutral mass symmetrically without describing it as a leaning answer.
  const lowMiddleLabel = neutralTotal > 0 ? `${displayLabels?.leansLow || 'Low'} + ½ ${displayLabels?.neutral || 'neutral'}` : (displayLabels?.leansLow || 'Leans low')
  const highMiddleLabel = neutralTotal > 0 ? `${displayLabels?.leansHigh || 'High'} + ½ ${displayLabels?.neutral || 'neutral'}` : (displayLabels?.leansHigh || 'Leans high')
  const rawSegments = {
    negativeStrong: lowest,
    negativeSoft: Math.max(0, lowerTotal - lowest) + neutralTotal / 2,
    positiveSoft: Math.max(0, upperTotal - highest) + neutralTotal / 2,
    positiveStrong: highest,
  }

  return {
    dataStatus: normalized.some((bucket) => bucket.value > 0) ? 'available' as const : 'empty' as const,
    metricLabels: endLabels,
    /** The end labels are value ranges of a midpoint split, not policy band names. */
    rangeLabelled: !isPolicySplit,
    metricValues: reversed ? [lowerTotal, neutralTotal, upperTotal] : [upperTotal, neutralTotal, lowerTotal],
    legendLabels: reversed
      ? [lowLabel, displayLabels?.leansLow || 'Leans low', displayLabels?.leansHigh || 'Leans high', highLabel]
      : [highLabel, displayLabels?.leansHigh || 'Leans high', displayLabels?.leansLow || 'Leans low', lowLabel],
    geometryLegendLabels: reversed
      ? [lowLabel, lowMiddleLabel, highMiddleLabel, highLabel]
      : [highLabel, highMiddleLabel, lowMiddleLabel, lowLabel],
    segments: reversed ? {
      negativeStrong: rawSegments.positiveStrong,
      negativeSoft: rawSegments.positiveSoft,
      positiveSoft: rawSegments.negativeSoft,
      positiveStrong: rawSegments.negativeStrong,
    } : rawSegments,
    bandSegments: bandSegmentsLowToHigh(
      endLabels,
      reversed ? [lowerTotal, neutralTotal, upperTotal] : [upperTotal, neutralTotal, lowerTotal],
      reversed,
    ),
  }
}

/** Layout consumes measured groups; it never estimates them from histogram bins. */
function splitMeasuredStatistics(statistics: RatingStatistics, scale: RatingLikertSlideData['scale']) {
  const values = [statistics.favorablePercent, statistics.middlePercent, statistics.unfavorablePercent]
  const validWire = isRatingStatistics(statistics)
  const bounds = validWire ? ratingBounds(statistics.unit) : null
  const validDefinition = validWire && bounds?.[0] === scale.min && bounds?.[1] === scale.max
    && (scale.kind == null || scale.kind === (statistics.kind === 'nps' ? 'nps' : 'rating'))
    && (scale.polarity == null || scale.polarity === statistics.polarity)
    // A middle-is-best read's unfavourable share holds both ends: no three-band bar draws it.
    && statistics.polarity !== 'middle_is_best'
  const empty = validDefinition && statistics.baseSize === 0 && values.every((value) => value === null)
  const validValues = values.every((value) => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 100)
  const total = values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
  const available = validDefinition && statistics.baseSize > 0 && validValues && Math.abs(total - 100) <= 0.00001
  const [favorable, middle, unfavorable] = available ? values as number[] : [0, 0, 0]
  const labels = statistics.labels
  const metricLabels = [labels?.favorable, labels?.middle, labels?.unfavorable]
    .map((label, index) => typeof label === 'string' && label.trim() ? label : ['High ratings', 'Middle band', 'Low ratings'][index])
  return {
    dataStatus: available ? 'available' as const : empty ? 'empty' as const : 'invalid' as const,
    metricLabels,
    metricValues: [favorable, middle, unfavorable],
    legendLabels: [metricLabels[0], metricLabels[1], metricLabels[1], metricLabels[2]],
    geometryLegendLabels: [metricLabels[0], `½ ${metricLabels[1]}`, `½ ${metricLabels[1]}`, metricLabels[2]],
    segments: {
      negativeStrong: unfavorable,
      negativeSoft: middle / 2,
      positiveSoft: middle / 2,
      positiveStrong: favorable,
    },
    bandSegments: bandSegmentsLowToHigh(
      metricLabels, [favorable, middle, unfavorable], statistics.polarity === 'lower_is_better',
    ),
  }
}

export interface RatingBarSource {
  segments?: unknown
  buckets: Array<RatingBucket>
  scale: RatingLikertSlideData['scale']
  ratingStatistics?: RatingStatistics | null
}

export interface RatingBarResolution {
  /** The measured groups, still needed for the Favorable / Neutral / Unfavorable read. */
  split: ReturnType<typeof splitRatingBuckets>
  dataStatus: ReturnType<typeof splitRatingBuckets>['dataStatus']
  plan: RatingBarPlan | null
  labels: RatingBandLabels
  /** True when the bar came from the builder's `segments` rather than the legacy split. */
  stamped: boolean
}

/**
 * The one place a bar is decided, for the slide, its rows and both drilldowns.
 *
 * A payload carrying `segments` is drawn exactly as stamped. Anything else -- a
 * deck generated before the field existed, a drilldown response served from a
 * cache that predates it, an empty base -- falls to the legacy split, which is
 * folded into the same three-band shape so it draws through the same renderer.
 *
 * Stamped segments outrank an `unsupported` split: the builder resolved the unit
 * before it could band anything, so the ambiguity that retires an untyped 0-10
 * histogram does not apply to a payload it wrote.
 */
export function resolveRatingBar(
  source: RatingBarSource,
  displayLabels?: RatingLikertSlideData['displayLabels'],
): RatingBarResolution {
  const split = splitRatingBuckets(
    source.buckets, source.scale, displayLabels, source.ratingStatistics,
  )
  const stamped = readRatingSegments(source.segments)
  // Stamped segments carry the builder's policy bands, so their ends ARE the
  // favorable / unfavorable bands; the midpoint split's range labels describe
  // only the legacy bar it draws itself.
  const unitLabels = stamped && 'rangeLabelled' in split && split.rangeLabelled ? undefined : split.metricLabels
  const labels = ratingBandLabels(source.ratingStatistics, displayLabels, unitLabels)
  const plan = planRatingBar(stamped ?? split.bandSegments, labels, source.ratingStatistics?.labels)
  // Mixed display bins can cross scoring thresholds. Their geometry cannot
  // replace the measured net, whose contract and scale the split validates.
  if (plan && source.ratingStatistics && split.dataStatus === 'available') {
    plan.net = split.metricValues[0] - split.metricValues[2]
  }
  return {
    split,
    dataStatus: stamped ? 'available' : split.dataStatus,
    plan,
    labels,
    stamped: Boolean(stamped),
  }
}
